/**
 * Persist an in-progress Schrijven session ID in localStorage so the
 * user can resume after an accidental navigation / browser crash.
 *
 * Key format: `schrijven:resume:<mode>` — one slot per mode so an exam
 * and a practice session don't clobber each other.
 */

const PREFIX = "schrijven:resume:";
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24 h

interface ResumeEntry {
  sessionId: string;
  savedAt: number;
}

function key(mode: "exam" | "practice") {
  return `${PREFIX}${mode}`;
}

function isLocalStorageAvailable(): boolean {
  try {
    return typeof localStorage !== "undefined";
  } catch {
    return false;
  }
}

export function saveResumableSession(
  mode: "exam" | "practice",
  sessionId: string,
): void {
  if (!isLocalStorageAvailable()) return;
  const entry: ResumeEntry = { sessionId, savedAt: Date.now() };
  localStorage.setItem(key(mode), JSON.stringify(entry));
}

export function loadResumableSession(
  mode: "exam" | "practice",
): string | null {
  if (!isLocalStorageAvailable()) return null;
  const raw = localStorage.getItem(key(mode));
  if (!raw) return null;
  try {
    const entry = JSON.parse(raw) as ResumeEntry;
    if (Date.now() - entry.savedAt > MAX_AGE_MS) {
      localStorage.removeItem(key(mode));
      return null;
    }
    return entry.sessionId;
  } catch {
    localStorage.removeItem(key(mode));
    return null;
  }
}

export function clearResumableSession(mode: "exam" | "practice"): void {
  if (!isLocalStorageAvailable()) return;
  localStorage.removeItem(key(mode));
}

/** Remove entries older than MAX_AGE_MS. Call on app boot. */
export function sweepStaleResumeSessions(): void {
  if (!isLocalStorageAvailable()) return;
  for (const mode of ["exam", "practice"] as const) {
    const raw = localStorage.getItem(key(mode));
    if (!raw) continue;
    try {
      const entry = JSON.parse(raw) as ResumeEntry;
      if (Date.now() - entry.savedAt > MAX_AGE_MS) {
        localStorage.removeItem(key(mode));
      }
    } catch {
      localStorage.removeItem(key(mode));
    }
  }
}
